import { useMemo } from 'react'
import { isToday } from 'date-fns'

import { useDateStore } from '@/components/DateInput'
import { Score } from '@/components/Score'
import { Tooltip } from '@/ui/Tooltip'
import { useSharedRoutesStore, useWorkstationStore } from '../../store'
import { RouteDetails } from './RoutesDetails'
import { ReassignRouteAction } from './ReassignRouteAction'
import { sortRoutes } from '../../helpers/sortRoutes'
import { useCalculateAmountOfFitRoutes } from '../../hooks/useCalculateAmountOfFitRoutes'
import { useReaderLogStore } from '@/modules/FindControllerLog/store'

import styles from './Routes.module.scss'

import InformationIcon from '../../assets/information.svg'

const ROUTES_TO_HIDE_SCORE = 10
const LOW_FREQUENCY = 'LF'

interface Props {
    isEditable?: boolean
    isShared?: boolean
    isReadOnly?: boolean
}

export const Routes = ({ isEditable = true, isShared = false, isReadOnly = false }: Props) => {
    const { routes, reassignedRoutes } = useWorkstationStore()
    const { sharedRoutes } = useSharedRoutesStore()
    const { source } = useReaderLogStore()
    const { date } = useDateStore()

    const routesToRender = useMemo(() => {
        if (isShared) {
            return sharedRoutes
        }

        if (isReadOnly) {
            return source?.routes || []
        }

        return routes
    }, [sharedRoutes, source, routes])

    const isScoreVisible = isToday(date) && routesToRender.length < ROUTES_TO_HIDE_SCORE
    const { containerRef, fitCount } = useCalculateAmountOfFitRoutes({
        totalItems: routesToRender.length,
        isEditable,
    })

    const visibleRoutes = useMemo(() => {
        const sorted = sortRoutes(routesToRender)

        return sorted.slice(0, fitCount)
    }, [routesToRender, fitCount])

    const hiddenCount = routesToRender.length - visibleRoutes.length

    const checkIsRouteReassigned = (route: string) => !!reassignedRoutes.find((item) => item.route === route)

    if (!routesToRender.length) {
        return <>{isEditable && <ReassignRouteAction />}</>
    }
    return (
        <div ref={containerRef} className={styles.root}>
            {visibleRoutes.map((route) => {
                const isLowFrequencyRoute = route.routeFrequency === LOW_FREQUENCY

                return (
                    <Tooltip
                        key={route.route}
                        tooltip={<RouteDetails {...route} isReassigned={checkIsRouteReassigned(route.route)} />}
                        trigger="click"
                        placement="bottom"
                        isShown={isEditable || isReadOnly}
                    >
                        <div className={styles.route}>
                            <div className={styles.routeWrapper}>
                                <span className={styles.title}>{route.route}</span>
                                {checkIsRouteReassigned(route.route) && (
                                    <span className={styles.informationIconWrapper}>
                                        <InformationIcon />
                                    </span>
                                )}
                            </div>
                            {isScoreVisible &&
                                (route.isOldValue ? (
                                    <div>-</div>
                                ) : (
                                    <Score
                                        ewt={+route.ewt}
                                        mps={+route.mps}
                                        variance={+route.variance}
                                        value={+route.ewt}
                                        actual={route.actual}
                                        expected={route.expected}
                                        isInaccurate={
                                            route.isInaccurate || route.isOldValue || route.isOlderThan30Minutes
                                        }
                                        isLowFrequency={isLowFrequencyRoute}
                                    />
                                ))}
                        </div>
                    </Tooltip>
                )
            })}
            {!!hiddenCount && <span className={styles.hiddenCount}>+{hiddenCount}</span>}
            {isEditable && <ReassignRouteAction />}
        </div>
    )
}
